import styled from "styled-components";
import BackgroundImage from "../../../../assets/images/landing-page/investor-lp-hero.png";
import ExploreBackgroundImage from "../../../../assets/images/landing-page/explore-background.webp";
import { colorPalette, typeScale } from "../../../../utils/theme";
import ManagdInNumbersCardImage from "../../../../assets/images/landing-page/managd_stats_logo.svg";

export const Container = styled.div``;

// Hero Section
export const HeroSection = styled.section`
  margin: 0;
  padding-top: 100px;
  padding-bottom: 120px;
  background-image: url(${BackgroundImage});
  background-position: top center;
  background-repeat: no-repeat;
  background-size: 500px auto;

  @media (max-width: 767.98px) {
    padding-top: 48px;
    padding-bottom: 48px;
    padding-left: 16px;
    padding-right: 16px;
    background-size: 300px auto;
  }
`;

export const HeroContent = styled.div`
  text-align: center;
  justify-items: center;
`;

export const HeroBadge = styled.p`
  margin-bottom: 14px;
  color: ${colorPalette.blue500};
  ${typeScale.bodyLGMain};
`;

export const HeroTitle = styled.h1`
  margin-bottom: 42px;
  color: ${colorPalette.textMain};
  font-family: "Funnel Display", sans-serif;
  font-size: 4.225rem;
  font-weight: 500;
  line-height: 1;
  letter-spacing: -0.03em;

  @media (max-width: 767.98px) {
    margin-bottom: 8px;
    font-size: 2.125rem;
    line-height: 1.25;
  }
`;

export const HeroSubtitle = styled.p`
  margin-bottom: 42px !important;
  color: ${colorPalette.textDark} !important;
  ${typeScale.bodyMD};

  @media (max-width: 767.98px) {
    margin-bottom: 24px !important;
    color: ${colorPalette.textLight};
    ${typeScale.bodyXS};
  }
`;

export const HeroButtonWrapper = styled.div`
  display: flex;
  gap: 16px;
`;

// Features Section
export const FeatureSection = styled.section`
  @media (max-width: 767.98px) {
    padding-left: 16px;
    padding-right: 16px;
  }
`;

export const SectionBadge = styled.p`
  margin-bottom: 4px;
  text-transform: uppercase;
  color: ${colorPalette.blue500};
  ${typeScale.bodyXSMain};

  @media (max-width: 767.98px) {
    margin-bottom: 8px;
    ${typeScale.bodyXXSMain};
  }
`;

export const SectionTitle = styled.h2`
  margin-bottom: 16px;
  color: ${colorPalette.textMain};
  ${typeScale.headingMain};

  @media (max-width: 767.98px) {
    margin-bottom: 8px;
  }
`;

export const SectionSubtitle = styled.h2`
  margin-bottom: 32px;
  color: ${colorPalette.textDark};
  ${typeScale.bodyLG};

  @media (max-width: 767.98px) {
    margin-bottom: 24px;
    ${typeScale.bodySM};
  }
`;

export const FeatureCardsWrapper = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-top: 24px;
  padding-bottom: 24px;
  margin-left: -16px; /* Offset the section's padding */
  margin-right: -16px; /* Offset the section's padding */

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const FeatureCard = styled.div`
  height: 100%;
  flex: 1;
  border-radius: 16px;
  padding: 32px;
  background: #ebf1f733;
  border: 1px solid ${colorPalette.blue200};
  box-shadow: 0px 0px 30.27px 0px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(150px);
  text-align: start;

  @media (min-width: 768px) {
    transition: all 0.3s ease;
    cursor: pointer;

    &:hover {
      transform: translateY(-6px) scale(1.02);
      box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
    }
  }

  @media (max-width: 767.98px) {
    height: auto;
    flex: 0 0 auto;
    width: 85%;

    &:first-child {
      margin-left: 16px;
    }

    &:last-child {
      margin-right: 16px;
    }
  }
`;

export const FeatureIcon = styled.img`
  margin-bottom: 32px;
`;

export const FeatureTitle = styled.h4`
  margin-bottom: 12px;
  color: ${colorPalette.textMain};
  ${typeScale.headingSMMain};
`;

export const FeatureDescription = styled.p`
  margin-bottom: 0;
  color: ${colorPalette.textDark};
  ${typeScale.bodySM};
`;

export const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: start;
`;

export const FeatureListItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  gap: 8px;
`;

export const FeatureListItemLabel = styled.p`
  margin: 0;
  color: ${colorPalette.textMain};
  ${typeScale.bodySM};

  @media (max-width: 767.98px) {
    ${typeScale.bodyXS};
  }
`;

export const StatsSection = styled.section`
  padding-top: 28px;
  padding-bottom: 16px;

  @media (max-width: 767.98px) {
    padding-top: 24px;
    padding-bottom: 24px;
    padding: 16px;
    padding-right: 16px;
  }
`;

export const ManagdInNumbersCard = styled.div<{ showImage?: boolean }>`
  position: relative;
  padding: 32px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.6);
  border: 1.5px solid ${colorPalette.blue200};
  backdrop-filter: blur(150px);
  overflow: hidden;

  ${({ showImage }) =>
    showImage &&
    `
  &::after {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    width: 100%;
    height: 100%;
    background: url(${ManagdInNumbersCardImage}) no-repeat bottom right;
    background-size: contain;
      pointer-events: none;
    }
  `}

  @media (max-width: 767.98px) {
    padding: 16px;
  }
`;

export const StatsTitle = styled.h2`
  margin-top: 8px;
  margin-bottom: 16px;
  color: ${colorPalette.textMain};
  ${typeScale.headingMain};

  @media (max-width: 767.98px) {
    margin-bottom: 12px;
    ${typeScale.headingSMMain};
  }
`;

export const StatsSubtitle = styled.p`
  margin-bottom: 32px;
  color: ${colorPalette.textDark};
  ${typeScale.bodyMD};

  @media (max-width: 767.98px) {
    ${typeScale.bodyXS};
  }
`;

// How It Works Section
export const HowItWorksSection = styled.section`
  padding-top: 80px;
  padding-bottom: 80px;

  @media (max-width: 767.98px) {
    padding-top: 48px;
    padding-bottom: 48px;
    padding-left: 16px;
    padding-right: 16px;
  }
`;

export const HowItWorksStepsWrapper = styled.div`
  position: relative;
  margin-top: 48px;

  @media (max-width: 767.98px) {
    margin-top: 32px;
  }
`;

export const HowItWorksDesktopLine = styled.div`
  display: none;
`;

export const HowItWorksMobileLine = styled.div`
  position: absolute;
  top: 0;
  left: 40px;
  width: 2px;
  height: 100%;
  background-color: ${colorPalette.blue200};
  z-index: 0;

  @media (min-width: 768px) {
    display: none;
  }
`;

export const HowItWorksStep = styled.div`
  position: relative;
  text-align: center;
  z-index: 1;

  @media (max-width: 767.98px) {
    display: flex;
    align-items: center;
    margin-bottom: 24px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

export const HowItWorksIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: ${colorPalette.blue500};
  margin: 0 auto 24px auto;
  position: relative;
  z-index: 10;

  @media (max-width: 767.98px) {
    width: 48px;
    height: 48px;
    margin: 0 12px 0 16px;
    position: relative;
    z-index: 1;
    flex-shrink: 0;
  }
`;

export const HowItWorksStepBox = styled.div`
  position: relative;
  background: #ebf1f766;
  border: 1.5px solid ${colorPalette.blue200};
  border-radius: 16px;
  padding: 32px 16px;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.04);
  text-align: center;

  @media (min-width: 768px) {
    transition: all 0.3s ease;
    cursor: pointer;

    &:hover {
      transform: translateY(-6px) scale(1.015);
      box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
    }
  }

  @media (max-width: 767.98px) {
    background: #ebf1f766;
    border: 1px solid ${colorPalette.blue200};
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
    text-align: left;
    flex: 1;
  }
`;

export const HowItWorksStepTitle = styled.h4`
  margin-bottom: 12px;
  color: ${colorPalette.textMain};
  ${typeScale.headingXSMain};

  @media (max-width: 767.98px) {
    margin-bottom: 8px;
  }
`;

export const HowItWorksStepDescription = styled.p`
  margin: 0;
  color: ${colorPalette.textDark};
  ${typeScale.bodyXS};
  max-width: 280px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 767.98px) {
    max-width: none;
    margin-left: 0;
    margin-right: 0;
  }
`;

// Explore Section
export const ExploreSection = styled.section`
  padding-top: 80px;
  padding-bottom: 80px;
  /*background-image: url(${ExploreBackgroundImage});
  background-position: top center;
  background-repeat: no-repeat;*/

  @media (max-width: 767.98px) {
    padding-top: 48px;
    padding-bottom: 48px;
    padding-left: 16px;
    padding-right: 16px;
  }
`;

export const CTAButtonWrapper = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
`;

// Find New Home Section
export const FindNewHomeSection = styled.section`
  padding-top: 80px;
  padding-bottom: 80px;
  border-bottom: 1px solid ${colorPalette.blue100};

  @media (max-width: 767.98px) {
    padding-top: 48px;
    padding-bottom: 48px;
    padding-left: 16px;
    padding-right: 16px;
  }
`;

export const UnitCardsWrapper = styled.div`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding-top: 24px;
  padding-bottom: 24px;
  padding-left: 16px;
  padding-right: 16px;
  margin-left: -16px; /* Offset the section's padding */
  margin-right: -16px; /* Offset the section's padding */
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    display: none;
  }

  /* Ensure cards maintain minimum width on mobile */
  > * {
    flex: 0 0 380px; /* Fixed width for each card */
    scroll-snap-align: center;
  }
`;

export const UnitCard = styled.div`
  background: #ffffff66;
  border: 1.5px solid ${colorPalette.blue200};
  border-radius: 16px;
  transition: all 0.3s ease;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  @media (min-width: 768px) {
    &:hover {
      transform: translateY(-8px) scale(1.02);
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.1);
      border-color: ${colorPalette.blue300};
      background: #ffffff88;
    }
  }

  @media (max-width: 767.98px) {
    box-shadow: none;
  }
`;

export const UnitCardImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;

  @media (max-width: 767.98px) {
    height: 250px;
  }
`;

export const UnitCardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;

  @media (max-width: 767.98px) {
    height: 250px;
  }
`;

export const UnitCardTag = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
`;

export const UnitCardContent = styled.div`
  padding: 16px;
  text-align: start;
`;

export const UnitCardTitle = styled.h4`
  margin-bottom: 8px;
  color: ${colorPalette.textMain};
  ${typeScale.headingSMMain};
`;

export const UnitLocationWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${colorPalette.blue200};
`;

export const UnitLocation = styled.p`
  margin-bottom: 0;
  color: ${colorPalette.textDark};
  ${typeScale.bodyXXS};
`;

export const UnitStatistics = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

export const UnitStatisticItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;

  @media (max-width: 767.98px) {
    gap: 4px;
  }
`;

export const UnitStatisticItemLabel = styled.p`
  margin-bottom: 0;
  color: ${colorPalette.textDark};
  ${typeScale.bodyXSMain};

  @media (max-width: 767.98px) {
    ${typeScale.bodyXXSMain};
  }
`;

export const UnitCardsIndicators = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
  padding-bottom: 24px;
`;

export const UnitCardsIndicatorDot = styled.div<{ isActive: boolean }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${({ isActive }) =>
    isActive ? colorPalette.textMain : "#17224D33"};
  transition: background-color 0.3s ease;
  cursor: pointer;
`;
